import axiosInstance from "../";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {UserCreate} from "../../types/user/UserCreate";
import {IPartnerEmployee} from "../../types/partners/IPartnerEmployee";

export type IVacancyCompany = {
  id: string;
} & CreateVacancyCompanyDTO

export type CreateVacancyCompanyDTO = {
  name: string;
  description: string;
  code: string;
  companyId: string | number
  bin: string;
  lawName: string;
}

export type UpdateVacancyCompany = { id: string | number } & Partial<CreateVacancyCompanyDTO>;

export type BaseCountResponse = [IVacancyCompany[], number];

class VacancyCompanyApi {
  baseURL: string;

  constructor() {
    this.baseURL = `/api/vacancy-company`;
  }

  getAll = async (companyId?: number): Promise<BaseCountResponse> => {
    console.log(this.baseURL);
    const response = await axiosInstance.get<BaseCountResponse>(`${this.baseURL}/find`, { params: { companyId } });
    return response.data;
  };

  getById = async (id: string): Promise<IVacancyCompany> => {
    const response = await axiosInstance.get<IVacancyCompany>(`${this.baseURL}/find/${id}`);
    return response.data;
  };

  getByCode = async (code: string): Promise<IVacancyCompany> => {
    const response = await axiosInstance.get<IVacancyCompany>(`${this.baseURL}/find-code/${code}`);
    return response.data;
  };

  create = async (body: CreateVacancyCompanyDTO): Promise<IVacancyCompany> => {
    const response = await axiosInstance.post<IVacancyCompany>(`${this.baseURL}/create`, { ...body });
    return response.data;
  };

  update = async (body: UpdateVacancyCompany): Promise<IVacancyCompany> => {
    const response = await axiosInstance.patch<IVacancyCompany>(`${this.baseURL}/update/${body.id}`, { ...body });
    return response.data;
  };

  delete = async (currentVacancyCompanyId: number): Promise<string> => {
    const response = await axiosInstance.delete<string>(`${this.baseURL}/${currentVacancyCompanyId}`);
    return response.data;
  };

  getAllUsers = async (currentVacancyCompanyId: number): Promise<IPartnerEmployee[]> => {
    const response = await axiosInstance.get<IPartnerEmployee[]>(`${this.baseURL}/find-users/${currentVacancyCompanyId}`);
    return response.data;
  };

  async assignEmployee(companyId:number, userId:number, vacancyCompanyId: number): Promise<string> {
    const response = await axiosInstance.post<string>(`${this.baseURL}/assignEmployee/${companyId}`, {}, {params: {userId, vacancyCompanyId}});
    return response.data
  }

  async unAssignEmployee(companyId:number, userId:number, vacancyCompanyId: number): Promise<string> {
    const response = await axiosInstance.post<string>(`${this.baseURL}/unAssignEmployee/${companyId}`, {}, {params: {userId, vacancyCompanyId}});
    return response.data
  }

}


const vacancyCompanyService = new VacancyCompanyApi();

export const useVacancyCompanies = (companyId?: number) => {
  return useQuery({
    queryKey: ['vacancyCompanies', companyId],
    queryFn: () => vacancyCompanyService.getAll(companyId)
  });
};

export const useAllUsers = (currentVacancyCompanyId: number) => {
  return useQuery({
    queryKey: ['vacancyCompanyUsers', currentVacancyCompanyId],
    queryFn: () => vacancyCompanyService.getAllUsers(currentVacancyCompanyId),
    enabled: !!currentVacancyCompanyId,
  });
};

export const useAssignEmployee = () => {
  return useMutation({
    mutationFn: ({ companyId, userId, vacancyCompanyId }: { companyId: number; userId: number; vacancyCompanyId: number }) =>
      vacancyCompanyService.assignEmployee(companyId, userId, vacancyCompanyId),
  });
};


export const useUnAssignEmployee = () => {
  return useMutation({
    mutationFn: ({ companyId, userId, vacancyCompanyId }: { companyId: number; userId: number; vacancyCompanyId: number }) =>
      vacancyCompanyService.unAssignEmployee(companyId, userId, vacancyCompanyId),
  });
};


export const useDeleteVacancyCompanies = () => {
  return useMutation({
    mutationFn: (currentVacancyCompanyId: number) =>
      vacancyCompanyService.delete(currentVacancyCompanyId),
  });
};


export const useVacancyCompanyById = (id: string) => {
  return useQuery({
    queryKey: ['vacancyCompany', id],
    queryFn: () => vacancyCompanyService.getById(id),
    enabled: !!id,
  });
};

export const useVacancyCompanyByCode = (code: string) => {
  return useQuery({
    queryKey: ['vacancyCompanyByCode', code],
    queryFn: () => vacancyCompanyService.getByCode(code),
    enabled: !!code,
  });
};

export const useCreateVacancyCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vacancyCompanyService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacancyCompanies'] });
    }
  });
};

export const useUpdateVacancyCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vacancyCompanyService.update,
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['vacancyCompany', id] });
      queryClient.invalidateQueries({ queryKey: ['vacancyCompanies'] });
    }
  });
};


export const VacancyCompanyHook = {
  useVacancyCompanies,
  useVacancyCompanyById,
  useVacancyCompanyByCode,
  useCreateVacancyCompany,
  useUpdateVacancyCompany,
  useDeleteVacancyCompanies,
  useAllUsers,
  useAssignEmployee,
  useUnAssignEmployee
}


export class CreateVacancyCompany {
  id: string | number
  name:string
  description:string
  code:string
  lawName:string
  bin:string
  address: string
  phone: string

  constructor() {
    this.id = 0
    this.name = ''
    this.description = ''
    this.code = ''
    this.lawName = ''
    this.bin = ''
    this.address = ''
    this.phone = ''
  }
}